import React, { useState } from "react";
import { API_BASE_URL } from "../../config";
import {
    SIZES, money, monthlyCents, bookedDays, useEstimatorOptions,
} from "../Shared/estimatorShared";

// "What's your wall worth?" — the hook at the top of Become a host.
//
// --- what makes this different from EarningsEstimator.js -------------------
//
// No address. That is the whole point of it existing separately.
//
// The old estimator needs one because it was built around the road outside:
// geocode the address, read the vehicle count, price off that. It also means
// the first thing a business owner is asked for is their address, before they
// have been shown anything — identifying yourself to find out whether you are
// interested. Most people close the tab.
//
// The model no longer needs it. Value comes from who walks through the door
// and how long they stay, and this asks those two questions directly. Four
// selects, no typing, no personal information, and the address is asked for
// later in the application — by which point they have had an answer.
//
// --- the honesty constraints, which are the same ones ----------------------
//
//   * A RANGE, never a single figure. A point estimate reads as a quote.
//   * The monthly total carries the occupancy it assumes, and that assumption
//     is a control the host moves rather than a constant we assert. The
//     headline here IS the monthly figure -- it is the compelling number and
//     the one this page exists to put in someone's head -- so the thing that
//     makes it honest has to sit directly beneath it, not in a footnote.
//   * Visitor numbers are the host's own estimate and nothing measures them.
//   * "Could earn", never "will earn". Nothing here is an offer.

function Field({ label, value, onChange, children, hint }) {
    return (
        <label className="he__field">
            <span className="he__label">{label}</span>
            <select className="wb-select" value={value} onChange={onChange}>
                {children}
            </select>
            {hint && <span className="he__hint">{hint}</span>}
        </label>
    );
}

export default function HostEstimator({ onApply }) {
    const { venues, visitorBands, dwellBands, disclosure } = useEstimatorOptions();

    const [venue, setVenue] = useState("salon");
    const [visitors, setVisitors] = useState("");
    const [dwell, setDwell] = useState("");
    const [inches, setInches] = useState(43);

    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [occupancy, setOccupancy] = useState(null);

    const est = result && result.estimate;
    const occ = occupancy === null ? 0.25 : occupancy;
    const days = est ? bookedDays(occ, est.assumptions) : 0;

    async function submit(e) {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            const res = await fetch(`${API_BASE_URL}/api/estimate`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    screen_inches: inches,
                    venue_type: venue,
                    // Omitted rather than sent empty: "" is a validation error,
                    // leaving the key out is the documented way to say "not
                    // known", and the server's disclaimer changes to match.
                    ...(visitors ? { visitors_band: visitors } : {}),
                    ...(dwell ? { dwell_band: dwell } : {}),
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(data.message || "We could not work that one out.");
                return;
            }
            setResult(data);
            setOccupancy((current) =>
                current === null
                    ? ((data.estimate || {}).assumptions || {}).occupancy ?? 0.25
                    : current
            );
        } catch {
            setError("Network error. Nothing was sent.");
        } finally {
            setBusy(false);
        }
    }

    // The sentence under the figure. Built from what they actually answered,
    // so it reads back their own inputs -- which is what makes the result feel
    // like an assessment of THEIR space rather than a number off a table.
    // Anything they did not tell us is simply left out.
    function basisSentence() {
        const parts = [];
        const venueLabel = (venues.find((v) => v.value === venue) || {}).label;
        if (venueLabel) parts.push("a " + venueLabel.toLowerCase().split(",")[0]);
        const f = est.factors || {};
        if (f.visitors) parts.push(f.visitors.label.toLowerCase());
        if (f.dwell) {
            parts.push(
                f.dwell.source === "host"
                    ? `visits of ${f.dwell.label.toLowerCase()}`
                    : `visits of ${f.dwell.label.toLowerCase()} (typical here)`
            );
        }
        parts.push(`a ${inches}" screen`);
        return parts.join(", ");
    }

    if (est) {
        return (
            <div className="he he--result">
                <div className="he__result-head">
                    <p className="he__eyebrow">Your space could earn about</p>
                    <p className="he__figure">
                        {money(monthlyCents(est.daily_rate_low_cents, occ, est.assumptions))}
                        –
                        {money(monthlyCents(est.daily_rate_high_cents, occ, est.assumptions))}
                        <span className="he__figure-unit"> / month</span>
                    </p>
                    <p className="he__basis">Based on {basisSentence()}.</p>
                </div>

                {/* The assumption behind the headline, directly beneath it and
                    adjustable. A monthly total is a revenue forecast, and the
                    number of days it assumes is the single biggest lever on it
                    -- so that lever belongs to the reader, not to us. */}
                <div className="he__occ">
                    <label className="he__occ-label" htmlFor="he-occupancy">
                        If it’s booked{" "}
                        <strong>
                            {days === 0
                                ? "not at all"
                                : `about ${days} ${days === 1 ? "day" : "days"} a month`}
                        </strong>
                    </label>
                    <input
                        id="he-occupancy"
                        className="he__slider"
                        type="range"
                        min="0" max="100" step="5"
                        value={Math.round(occ * 100)}
                        onChange={(e) => setOccupancy(Number(e.target.value) / 100)}
                        aria-label="How many days a month the screen is booked"
                        aria-valuetext={
                            days === 0 ? "Not booked at all" : `About ${days} days a month`
                        }
                    />
                    <p className="he__rate">
                        At {money(est.daily_rate_low_cents)}–{money(est.daily_rate_high_cents)} a
                        day, after our{" "}
                        {Math.round((1 - est.assumptions.vendor_share) * 100)}% fee.
                    </p>
                </div>

                <div className="he__actions">
                    <button
                        type="button"
                        className="wb-btn wb-btn--primary wb-btn--lg wb-btn--block"
                        // Carries every answer forward. The application must not
                        // ask any of this again -- the two screens are one
                        // process, and re-asking is what makes a funnel feel
                        // like two unrelated forms.
                        onClick={() => onApply && onApply({
                            venue_type: venue,
                            venue_label: (venues.find((v) => v.value === venue) || {}).label,
                            visitors_band: visitors,
                            // Labels as well as keys. The application shows
                            // these back to the applicant and sends them to a
                            // human who reads the queue -- "250 to 500 a week"
                            // is what that person needs, not "250_500".
                            visitors_label: (est.factors.visitors || {}).label || "",
                            dwell_band: dwell,
                            dwell_label: (est.factors.dwell || {}).label || "",
                            screen_inches: inches,
                        })}
                    >
                        Become a WallBurst host
                    </button>
                    <button
                        type="button"
                        className="wb-btn wb-btn--ghost wb-btn--sm he__redo"
                        onClick={() => { setResult(null); setError(""); }}
                    >
                        Change my answers
                    </button>
                </div>

                <p className="he__disclaimer">{result.disclaimer}</p>
            </div>
        );
    }

    return (
        <form className="he" onSubmit={submit}>
            <div className="he__head">
                <h2 className="he__title">What’s your wall worth?</h2>
                <p className="he__sub">
                    Four questions, no sign-up. We’ll give you a range.
                </p>
            </div>

            <Field
                label="What kind of place is it?"
                value={venue}
                onChange={(e) => {
                    const next = e.target.value;
                    setVenue(next);
                    // Pre-fill dwell from the category. The default travels
                    // with each option from the server rather than being a
                    // second copy of the mapping here, which would drift.
                    const picked = venues.find((v) => v.value === next);
                    if (picked && picked.default_dwell) setDwell(picked.default_dwell);
                }}
            >
                {venues.map((v) => (
                    <option key={v.value} value={v.value}>{v.label}</option>
                ))}
            </Field>

            <Field
                label="Roughly how many people visit each week?"
                value={visitors}
                onChange={(e) => setVisitors(e.target.value)}
                hint={visitors ? disclosure : "A guess is fine — it makes the answer much better."}
            >
                <option value="">I’m not sure</option>
                {visitorBands.map((b) => (
                    <option key={b.value} value={b.value}>{b.label}</option>
                ))}
            </Field>

            <Field
                label="How long does a typical visit last?"
                value={dwell}
                onChange={(e) => setDwell(e.target.value)}
                hint="Time in front of the screen is worth more than people passing it."
            >
                <option value="">Use typical for this business</option>
                {dwellBands.map((b) => (
                    <option key={b.value} value={b.value}>{b.label}</option>
                ))}
            </Field>

            <Field
                label="Screen size"
                value={inches}
                onChange={(e) => setInches(Number(e.target.value))}
            >
                {SIZES.map((s) => (
                    <option key={s} value={s}>{s}&quot; diagonal</option>
                ))}
            </Field>

            {error && <p className="he__error">{error}</p>}

            <button
                type="submit"
                className="wb-btn wb-btn--primary wb-btn--lg wb-btn--block"
                disabled={busy}
            >
                {busy ? "Working it out…" : "Estimate my earnings"}
            </button>
        </form>
    );
}
